import {
	Box,
	Button,
	Flex,
	HStack,
	Text,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import API_URL from '../../constants/environment';
import SessionContext from '../../contexts/SessionContext';
import { useResyncPlatformLocations } from '../../hooks/useResyncPlatformLocations';
import { AgenticStepLoader } from '../AgenticStepLoader';
import { SupplierNetworkStep } from '../onboarding/SupplierNetworkStep';
import { LocationMappingUI, type CccRowMapping, type LocationMapping } from '../onboarding/LocationMappingUI';
import { useReconcileLocations, type CccLocation, type PartstraderLocation } from '../../hooks/useReconcileLocations';
import { useCreateTestingLocations } from '../../hooks/useCreateTestingLocations';
import { LocationCredentialsTable } from '../onboarding/LocationCredentialsTable';

type FlowStep = 'syncing' | 'mapping' | 'credentials' | 'supplier-mapping';

interface PtAdditiveFlowProps {
	onComplete: () => void;
	onCancel: () => void;
	mitchellCredentialLocationIds: Set<string>;
	opsCredentialLocationIds: Set<string>;
}

interface CreatedLocation {
	_id: string;
	title: string;
}

type CredentialTab = 'mitchell' | 'ops';

const SYNC_AGENTIC_STEPS = [
	{ label: 'Verifying credentials', description: 'Logging in to PartsTrader with the new credentials' },
	{ label: 'Pulling locations', description: 'Fetching CCC and PartsTrader locations' },
	{ label: 'Matching locations', description: 'Reconciling PartsTrader locations against CCC-backed locations' },
];

export const PtAdditiveFlow = ({
	onComplete,
	onCancel,
	mitchellCredentialLocationIds,
	opsCredentialLocationIds,
}: PtAdditiveFlowProps) => {
	const sessionContext = useContext(SessionContext);
	const toast = useToast();
	const headers = useMemo(
		() => ({ Authorization: sessionContext?.sessionId ?? '' }),
		[sessionContext?.sessionId],
	);

	const resync = useResyncPlatformLocations();
	const reconcile = useReconcileLocations();
	const createLocations = useCreateTestingLocations();

	const [step, setStep] = useState<FlowStep>('syncing');
	const [ptLocations, setPtLocations] = useState<PartstraderLocation[]>([]);
	const [cccLocations, setCccLocations] = useState<CccLocation[]>([]);
	const [cccMappings, setCccMappings] = useState<Record<string, CccRowMapping>>({});
	const [createdLocations, setCreatedLocations] = useState<CreatedLocation[]>([]);
	const [savingCreds, setSavingCreds] = useState(false);
	const [activeCredentialTab, setActiveCredentialTab] = useState<CredentialTab>('mitchell');
	const uploadModal = useDisclosure();

	const hasKickedOff = useRef(false);
	const hasReconciled = useRef(false);

	// Start a Settings-scoped onboarding continuation: CCC is always present and anchored
	// to existing location docs, while the newly-added PT credentials reveal new PT ids.
	useEffect(() => {
		if (hasKickedOff.current) return;
		hasKickedOff.current = true;
		resync.reset();
		(async () => {
			try {
				await resync.resync.mutateAsync({ targets: ['partstrader', 'ccc'] });
			} catch (err) {
				toast({
					title: 'Could not start integration sync',
					description: err instanceof Error ? err.message : 'Failed to start CCC/PT location sync.',
					status: 'error',
					duration: 5000,
					isClosable: true,
				});
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Once PT has persisted locations and CCC has completed its location pull, run the same
	// location reconciliation the create-account flow uses. Difference: we keep all CCC
	// locations (including claimed/existing CCC ids) visible, so createTestingLocations can
	// update existing location docs by CCC id instead of only creating new docs.
	useEffect(() => {
		if (!resync.locationsReady || hasReconciled.current) return;
		hasReconciled.current = true;

		const claimedPtSet = new Set(resync.claimedPtIds);
		const unclaimedPt = (resync.ptLocations ?? []).filter((pt) => !claimedPtSet.has(pt.id));
		const allCcc = resync.cccLocations ?? [];

		if (unclaimedPt.length === 0) {
			toast({
				title: 'No new PartsTrader locations',
				description: 'These credentials don\'t expose any locations beyond the ones already linked.',
				status: 'info',
				duration: 4000,
				isClosable: true,
			});
			onComplete();
			return;
		}

		setPtLocations(unclaimedPt);
		setCccLocations(allCcc);

		(async () => {
			let suggested: LocationMapping[] = [];
			try {
				const result = await reconcile.mutateAsync({
					partstraderLocations: unclaimedPt,
					cccLocations: allCcc,
				});
				suggested = result.mappings;
			} catch {
				// Best-effort; leave rows disabled unless a PT id was confidently mapped.
			}

			const initial: Record<string, CccRowMapping> = {};
			for (const ccc of allCcc) {
				initial[ccc.id] = { enabled: false, ptIds: [] };
			}
			for (const mapping of suggested) {
				if (!initial[mapping.cccId]) continue;
				initial[mapping.cccId] = {
					enabled: true,
					ptIds: [...initial[mapping.cccId].ptIds, mapping.partstraderId],
				};
			}
			setCccMappings(initial);
			setStep('mapping');
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resync.locationsReady, resync.ptLocations]);

	const getSyncCurrentStep = (): number => {
		if (resync.resync.isPending) return 0;
		if (!resync.locationsReady) return 1;
		return 2;
	};

	const handleConfirmMapping = async (confirmedMappings: LocationMapping[]) => {
		if (confirmedMappings.length === 0) {
			toast({
				title: 'No locations selected',
				description: 'Map at least one PartsTrader location to a CCC-backed location.',
				status: 'warning',
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		try {
			const cccMap = new Map(cccLocations.map((c) => [c.id, c]));
			const result = await createLocations.mutateAsync({
				organizationId: sessionContext?.organizationId ?? '',
				mappings: confirmedMappings.map((m) => {
					const ccc = cccMap.get(m.cccId);
					return {
						partstraderId: m.partstraderId,
						cccId: m.cccId,
						title: ccc?.name,
						address: ccc?.address,
						city: ccc?.city,
						state: ccc?.state,
						zip: ccc?.zip,
					};
				}),
			});

			toast({
				title: 'PartsTrader locations updated',
				description: `${result.locations.length} location${result.locations.length === 1 ? '' : 's'} updated or created.`,
				status: 'success',
				duration: 4000,
				isClosable: true,
			});

			setCreatedLocations(result.locations);
			const missingMitchell = result.locations.filter((loc) => !mitchellCredentialLocationIds.has(loc._id));
			const missingOps = result.locations.filter((loc) => !opsCredentialLocationIds.has(loc._id));
			const locationsNeedingCreds = [...missingMitchell, ...missingOps];
			if (locationsNeedingCreds.length > 0) {
				setActiveCredentialTab(missingMitchell.length > 0 ? 'mitchell' : 'ops');
				setStep('credentials');
			} else {
				setStep('supplier-mapping');
			}
		} catch (err) {
			toast({
				title: 'Failed to apply mappings',
				description: err instanceof Error ? err.message : 'Unknown error',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	};

	const mitchellCredentialLocations = createdLocations.filter((loc) => !mitchellCredentialLocationIds.has(loc._id));
	const opsCredentialLocations = createdLocations.filter((loc) => !opsCredentialLocationIds.has(loc._id));

	const handleSaveProviderCredentials = async (
		provider: 'mitchell' | 'opstrax',
		credentials: Array<{ locationId: string; username: string; password: string }>,
	) => {
		if (credentials.length === 0) {
			if (provider === 'mitchell') setActiveCredentialTab('ops');
			else setStep('supplier-mapping');
			return;
		}
		setSavingCreds(true);
		try {
			await axios.post(
				`${API_URL}/settings/account/integrations`,
				{ action: 'upsert', provider, credentials },
				{ headers },
			);
			toast({
				title: 'Credentials saved',
				description: `${credentials.length} ${provider === 'mitchell' ? 'Mitchell' : 'OPSTrax'} credential${credentials.length === 1 ? '' : 's'} saved.`,
				status: 'success',
				duration: 4000,
				isClosable: true,
			});

			if (provider === 'mitchell') setActiveCredentialTab('ops');
			else setStep('supplier-mapping');
		} catch {
			toast({
				title: 'Failed to save credentials',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setSavingCreds(false);
		}
	};

	const handleClose = () => {
		resync.reset();
		onCancel();
	};

	return (
		<Flex
			direction="column"
			position="fixed"
			inset={0}
			zIndex={1400}
			bg="white"
			px={10}
			py={8}
			minH="100vh"
			h="100vh"
			overflow="hidden"
		>
			<HStack justify="space-between" mb={4} flexShrink={0}>
				<Box>
					<Flex align="center" gap={3}>
						<Button size="sm" variant="outline" onClick={handleClose}>
							Back to Integrations
						</Button>
						<Box>
							<Text fontSize="lg" fontWeight="bold" color="gray.800">
								PartsTrader Setup
							</Text>
							<Text fontSize="sm" color="gray.500">
								Continue onboarding with CCC-backed location matching, optional credentials, and supplier reconciliation.
							</Text>
						</Box>
					</Flex>
				</Box>
			</HStack>

			<Box
				flex={1}
				minH={0}
				borderWidth={1}
				borderColor="gray.200"
				borderRadius="xl"
				p={6}
				bg="white"
				boxShadow="none"
				overflow="hidden"
			>
				{step === 'syncing' && (
					<Flex align="center" justify="center" h="100%">
						<AgenticStepLoader
							title="Pulling CCC and PartsTrader Locations"
							subtitle="Verifying credentials, pulling both location lists, and preparing location matching."
							steps={SYNC_AGENTIC_STEPS}
							currentStep={getSyncCurrentStep()}
						/>
					</Flex>
				)}

				{step === 'mapping' && (
					<LocationMappingUI
						partstraderLocations={ptLocations}
						cccLocations={cccLocations}
						suggestedMappings={[]}
						cccMappings={cccMappings}
						onConfirm={handleConfirmMapping}
						isConfirmLoading={createLocations.isPending}
						onToggleCcc={(cccId) => {
							setCccMappings((prev) => ({
								...prev,
								[cccId]: { ...prev[cccId], enabled: !prev[cccId]?.enabled },
							}));
						}}
						onAddPt={(cccId, ptId) => {
							setCccMappings((prev) => ({
								...prev,
								[cccId]: { ...prev[cccId], ptIds: [...(prev[cccId]?.ptIds ?? []), ptId] },
							}));
						}}
						onRemovePt={(cccId, ptId) => {
							setCccMappings((prev) => ({
								...prev,
								[cccId]: { ...prev[cccId], ptIds: (prev[cccId]?.ptIds ?? []).filter((id) => id !== ptId) },
							}));
						}}
						onBack={handleClose}
						usesPt
					/>
				)}

				{step === 'credentials' && (
					<Flex direction="column" width="100%" flex={1} minH={0}>
						<Flex justify="space-between" align="center" mb={4} flexShrink={0}>
							<Box>
								<Text fontSize="md" fontWeight="semibold">Platform Logins</Text>
								<Text fontSize="sm" color="gray.500">
									Add missing Mitchell or OPSTrax credentials for the updated locations. Existing usernames are prefilled; enter a password only when rotating or adding credentials.
								</Text>
							</Box>
							<Button size="sm" variant="outline" onClick={uploadModal.onOpen}>
								Upload Template
							</Button>
						</Flex>

						<HStack spacing={3} width="100%" mb={4} flexShrink={0}>
							{([
								{ key: 'mitchell' as const, label: 'Mitchell' },
								{ key: 'ops' as const, label: 'OPSTrax' },
							]).map((tab, idx) => {
								const isActive = activeCredentialTab === tab.key;
								const isCompleted = tab.key === 'mitchell' && activeCredentialTab === 'ops';
								return (
									<Box
										key={tab.key}
										flex={1}
										cursor="pointer"
										onClick={() => setActiveCredentialTab(tab.key)}
									>
										<Text
											fontSize="sm"
											fontWeight="medium"
											color={isActive || isCompleted ? '#0071E3' : 'gray.400'}
											mb={1.5}
										>
											{idx + 1}. {tab.label}
										</Text>
										<Box
											h="4px"
											borderRadius="full"
											bg={isActive || isCompleted ? '#0071E3' : 'gray.200'}
											transition="background 0.2s"
										/>
									</Box>
								);
							})}
						</HStack>

						{activeCredentialTab === 'mitchell' && (
							<LocationCredentialsTable
								locations={mitchellCredentialLocations}
								provider="mitchell"
								isLoading={savingCreds}
								isUploadOpen={uploadModal.isOpen}
								onCloseUpload={uploadModal.onClose}
								onBack={() => setStep('mapping')}
								onSkip={() => setActiveCredentialTab('ops')}
								onSave={(credentials) => handleSaveProviderCredentials('mitchell', credentials)}
							/>
						)}

						{activeCredentialTab === 'ops' && (
							<LocationCredentialsTable
								locations={opsCredentialLocations}
								provider="opstrax"
								isLoading={savingCreds}
								isUploadOpen={uploadModal.isOpen}
								onCloseUpload={uploadModal.onClose}
								onBack={() => setActiveCredentialTab('mitchell')}
								onSkip={() => setStep('supplier-mapping')}
								onSave={(credentials) => handleSaveProviderCredentials('opstrax', credentials)}
							/>
						)}
					</Flex>
				)}

					{step === 'supplier-mapping' && sessionContext?.organizationId && (
						<Box px={6} pb={4} h="100%" display="flex" flexDirection="column">
							<SupplierNetworkStep
								organizationId={sessionContext.organizationId}
								onComplete={onComplete}
								onSkip={onComplete}
								usesPt
							/>
						</Box>
					)}
			</Box>

				{step === 'mapping' && (
					null
				)}

		</Flex>
	);
};
